import React, { Dispatch, SetStateAction, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { OrderDA } from '../../services/admin/order.service';
import { Error } from '../../models/error.model';
import { AppService } from '../../models/app.service.model';
import { Company } from '../../models/company.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Order } from '../../models/order.model';
import { legendInvalid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { evaluateLegendRequired } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLegend from '../element/Legend';

export interface AppModalOrderNotificationProps {
  appServiceForUser: AppService | null,
  companyForUser: Company | null,
  order: Order | null,
  setOrder: Dispatch<SetStateAction<Order | null>>
};

let errorResponse: Error, orderResponse: Order;

const AppModalOrderNotification: React.FunctionComponent<AppModalOrderNotificationProps> = ({appServiceForUser, companyForUser, order, setOrder}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [comment, setComment] = useState({value: '', valid: false})

  const submitOrderStatus = async () => {
    setLoadIndicator('on')

    if (appServiceForUser && companyForUser && order && comment.valid) {
      let statusOrder = 'canceled'

      await OrderDA.putOrderStatus(companyForUser.id, order.id, statusOrder, comment.value).then( async (response) => {
        if (response.status === 200) {
          orderResponse = response.data

          setOrder(orderResponse)

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedStatus,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalOrderExport()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!comment.value.length) legendInvalid('modal-order-notification-legend-comment-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeComment = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setComment({...comment, value: event.target.value})
  }

  const validateComment = () => {
    evaluateLegendRequired(expressions.text, comment, setComment, 'modal-order-notification-legend-comment-valid', 'modal-order-notification-legend-comment-required')
  }

  const executeHideModalOrderExport = () => {
    modalHide('modal-order-notification')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-order-notification-legend-comment-valid')
    legendValidInvalidRestart('modal-order-notification-legend-comment-required')
    setComment({value: '', valid: false})
  }

  return (
    <div id="modal-order-notification" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.orderNotification}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="text-center fs-6 mb-5">
                <h5>{lang.labels.youWantToChangeOrderStatusToCanceled}</h5>
                <label>{lang.labels.youWillNotBeAbleToUndo}</label>
              </div>
              <div className="fv-row">
                <label className="form-label required">{lang.labels.message}</label>
                <TextareaAutosize className="form-control" name="comment" minRows={2} maxRows={3} value={comment.value} onChange={handleChangeComment} onKeyUp={validateComment} onBlur={validateComment} />
                <AppLegend component={"modal-order-notification"} attribute={{check: comment.valid, name: "comment", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitOrderStatus}>
              <span className="indicator-label">{lang.labels.yesCanceled}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalOrderExport}>{lang.labels.noCancel}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalOrderNotification;
